import React from 'react';
import { useLocation } from 'react-router-dom';
import { Database, Search, Terminal, Bookmark, History, ShieldCheck, SlidersHorizontal } from 'lucide-react';
import { useRedirect } from '../../../Shared/Hooks/useRedirect ';
import { storage } from '../../../Shared/Utils/authStorage';
import { dataServiceBase, CONFIGURED_ROLES, isConfiguredRole } from './dataServiceRoutes';
import { useDataServiceAccess } from './useDataServiceAccess';

/**
 * `requires` names a capability the server decides, so a tab is only shown when
 * the caller's own configuration grants it. The super admin and a delegate are
 * data-service roles and are never blocked by these flags; `undefined` means the
 * tab is always eligible.
 */
type Capability = 'queryBuilder' | 'console';

const TABS: {
  label: string;
  slug: string;
  icon: typeof Database;
  superAdminOnly?: boolean;
  hiddenFor?: string[];
  requires?: Capability;
}[] = [
  // Registering and editing connections is an admin job, so the configured roles
  // never see this tab. Their entry point is the query builder, which is why
  // their bare base path also resolves to the builder.
  { label: 'Connections', slug: '', icon: Database, hiddenFor: CONFIGURED_ROLES },
  { label: 'Query Builder', slug: 'query', icon: Search, requires: 'queryBuilder' },
  // Raw SQL is handed out per person through Team Access, on top of the
  // connection-level flag, so it is no longer super-admin only.
  { label: 'SQL Console', slug: 'console', icon: Terminal, requires: 'console' },
  { label: 'Saved Queries', slug: 'saved-queries', icon: Bookmark },
  { label: 'History', slug: 'history', icon: History },
  { label: 'Access', slug: 'access', icon: ShieldCheck, superAdminOnly: true },
  // A recruiter manages their own company's recruiters from the same screen, so
  // this is not super-admin only. A candidate is never a target and never sees it.
  { label: 'Team Access', slug: 'config', icon: SlidersHorizontal, hiddenFor: ['CANDIDATE'] },
];

const DataServiceTabNav = () => {
  const location = useLocation();
  const redirect = useRedirect();
  const role = (storage.getRole() || '').toUpperCase();
  const isSuperAdmin = role === 'SUPER_ADMIN';
  const isDelegate = role === 'DELEGATED_ADMIN';
  const configuredRole = isConfiguredRole(role);

  // Only the configured roles have per-person grants; the data-service roles
  // keep every tab their role already allows.
  const { queryBuilderAvailable, consoleAvailable } = useDataServiceAccess();

  // A delegate is routed under /data-service, a super admin under /admin/data-service.
  const basePath = dataServiceBase();

  const granted = (tab: { requires?: Capability }) => {
    if (!tab.requires) return true;
    if (isSuperAdmin || isDelegate) return true;
    return tab.requires === 'queryBuilder' ? queryBuilderAvailable : consoleAvailable;
  };

  /**
   * Where each tab points, and which paths count as "this tab is open".
   *
   * <p>The configured roles have no connections tab, so their bare base path
   * resolves to the query builder. The builder therefore has to treat that base
   * path as itself, or the tab would never highlight for the URL a user
   * actually lands on after clicking the navigation entry. When the builder is
   * not granted, the base path falls to the first tab that is still visible, so
   * the entry point is never a page the caller cannot open.</p>
   *
   * <p>The remap is scoped to those roles on purpose. The data-service roles do
   * have a connections tab, so leaving it on the bare base path is what keeps
   * the two tabs on distinct URLs. Remapping for them too gave Connections and
   * Query Builder the same path, which highlighted both tabs at once and always
   * loaded the builder.</p>
   */
  const visible = TABS.filter(
    (tab) =>
      (!tab.superAdminOnly || isSuperAdmin) &&
      !(tab.hiddenFor ?? []).includes(role) &&
      granted(tab)
  );

  const fallbackSlug = visible.find((tab) => tab.slug !== '')?.slug ?? '';
  const baseTabSlug = configuredRole
    ? granted({ requires: 'queryBuilder' })
      ? 'query'
      : fallbackSlug
    : '';

  const tabs = visible.map((tab) => {
    const slug = tab.slug === '' ? baseTabSlug : tab.slug;
    return {
      ...tab,
      path: slug ? `${basePath}/${slug}` : basePath,
      matchPaths: configuredRole && tab.slug === 'query' && slug === 'query' ? [basePath] : [],
    };
  });

  return (
    <div className="mx-auto mb-6 flex w-full max-w-[1600px] flex-wrap gap-2 rounded-2xl border border-slate-800 bg-slate-900/60 p-2">
      {tabs.map((tab) => {
        const active =
          tab.matchPaths.includes(location.pathname) ||
          location.pathname === tab.path ||
          (tab.path !== basePath && location.pathname.startsWith(`${tab.path}/`));
        const Icon = tab.icon;
        return (
          <button
            key={tab.path}
            onClick={() => redirect(tab.path)}
            className={`inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-xs font-black uppercase tracking-wider transition ${
              active
                ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-slate-950 shadow-lg shadow-cyan-500/20'
                : 'border border-slate-800 bg-slate-950/60 text-slate-400 hover:border-cyan-500/40 hover:text-cyan-400'
            }`}
          >
            <Icon size={14} />
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};

export default DataServiceTabNav;
